import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Search,
  X,
  Plus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Building2,
  Phone,
  Mail,
  MapPin,
  User,
  Pencil,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Save,
} from 'lucide-react';
import { SuppliersService, type Proveedor } from '../services/suppliers/suppliers.service';
import { usePermissions } from '../hooks/usePermissions';

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface SupplierFormData {
  nombre: string;
  nit: string;
  telefono: string;
  email: string;
  direccion: string;
  contacto: string;
  activo: boolean;
}

/* ─── Drawer ─── */
function SupplierDrawer({
  isOpen,
  onClose,
  supplier,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  supplier: Proveedor | null;
  onSubmit: (data: SupplierFormData) => void;
  isSubmitting: boolean;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState<SupplierFormData>({
    nombre: '',
    nit: '',
    telefono: '',
    email: '',
    direccion: '',
    contacto: '',
    activo: true,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof SupplierFormData, string>>>({});

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (supplier) {
      setFormData({
        nombre: supplier.nombre,
        nit: supplier.nit || '',
        telefono: supplier.telefono || '',
        email: supplier.email || '',
        direccion: supplier.direccion || '',
        contacto: supplier.contacto || '',
        activo: supplier.activo,
      });
    } else {
      setFormData({
        nombre: '',
        nit: '',
        telefono: '',
        email: '',
        direccion: '',
        contacto: '',
        activo: true,
      });
    }
    setErrors({});
  }, [supplier, isOpen]);

  const handleChange = (field: keyof SupplierFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Partial<Record<keyof SupplierFormData, string>> = {};
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es obligatorio';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(formData);
  };

  if (!isOpen) return null;

  const isEditMode = !!supplier;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-xl bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditMode ? 'Modifica los datos del proveedor' : 'Completa la información del proveedor'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => handleChange('nombre', e.target.value)}
              placeholder="Ej: Distribuidora SaludTotal"
              className={`block w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all ${
                errors.nombre ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.nombre && <p className="mt-1 text-sm text-red-500">{errors.nombre}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* NIT */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">NIT</label>
              <input
                type="text"
                value={formData.nit}
                onChange={(e) => handleChange('nit', e.target.value)}
                placeholder="Ej: 900.123.456-7"
                className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
              />
            </div>
            {/* Teléfono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input
                type="text"
                value={formData.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                placeholder="Ej: 300 123 4567"
                className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Ej: contacto@proveedor.com"
                className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
              />
            </div>
            {/* Contacto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contacto</label>
              <input
                type="text"
                value={formData.contacto}
                onChange={(e) => handleChange('contacto', e.target.value)}
                placeholder="Nombre del contacto"
                className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
              />
            </div>
          </div>

          {/* Dirección */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
            <textarea
              value={formData.direccion}
              onChange={(e) => handleChange('direccion', e.target.value)}
              placeholder="Dirección del proveedor"
              rows={2}
              className="block w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all resize-none"
            />
          </div>

          {/* Activo */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <input
              id="activo"
              type="checkbox"
              checked={formData.activo}
              onChange={(e) => handleChange('activo', e.target.checked)}
              className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
            />
            <label htmlFor="activo" className="text-sm font-medium text-gray-700 cursor-pointer">
              Proveedor activo
            </label>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Actualizar' : 'Guardar'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Delete Modal ─── */
function DeleteModal({
  supplier,
  onClose,
  onConfirm,
  isDeleting,
}: {
  supplier: Proveedor;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      <div
        className={`relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 transform transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Eliminar Proveedor</h3>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          ¿Estás seguro de que deseas eliminar al proveedor{' '}
          <span className="font-semibold text-gray-900">{supplier.nombre}</span>? Esta acción no se
          puede deshacer.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              onConfirm();
              handleClose();
            }}
            disabled={isDeleting}
            className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Eliminar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function ProveedoresPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission, user } = usePermissions();


  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState<Proveedor | null>(null);
  const [supplierToDelete, setSupplierToDelete] = useState<Proveedor | null>(null);

  // Debounce search 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Query suppliers
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<{
    data: Proveedor[];
    meta: PaginationMeta;
  }>({
    queryKey: ['suppliers', page, limit, debouncedSearch],
    queryFn: async () => {
      const response = await SuppliersService.list(page, limit, debouncedSearch);
      return response.data;
    },
  });

  const suppliers = data?.data || [];
  const paginationMeta = data?.meta;

  // Keep focus on search input
  useEffect(() => {
    if (!isFetching && searchTerm && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isFetching, searchTerm]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (formData: SupplierFormData) =>
      SuppliersService.create({
        nombre: formData.nombre,
        nit: formData.nit || undefined,
        telefono: formData.telefono || undefined,
        email: formData.email || undefined,
        direccion: formData.direccion || undefined,
        contacto: formData.contacto || undefined,
        activo: formData.activo,
      }),
    onSuccess: () => {
      toast.success('Proveedor creado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setIsCreateOpen(false);
      setSupplierToEdit(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Error al crear el proveedor');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      formData,
    }: {
      id: string;
      formData: SupplierFormData;
    }) =>
      SuppliersService.update(id, {
        nombre: formData.nombre,
        nit: formData.nit || undefined,
        telefono: formData.telefono || undefined,
        email: formData.email || undefined,
        direccion: formData.direccion || undefined,
        contacto: formData.contacto || undefined,
        activo: formData.activo,
      }),
    onSuccess: () => {
      toast.success('Proveedor actualizado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setIsCreateOpen(false);
      setSupplierToEdit(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Error al actualizar el proveedor');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => SuppliersService.delete(id),
    onSuccess: () => {
      toast.success('Proveedor eliminado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setSupplierToDelete(null);
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Error al eliminar el proveedor');
    },
  });

  const handleOpenCreate = () => {
    setSupplierToEdit(null);
    setIsCreateOpen(true);
  };

  const handleOpenEdit = (supplier: Proveedor) => {
    setSupplierToEdit(supplier);
    setIsCreateOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsCreateOpen(false);
    setTimeout(() => setSupplierToEdit(null), 300);
  };

  const handleSubmit = (formData: SupplierFormData) => {
    if (supplierToEdit) {
      updateMutation.mutate({ id: supplierToEdit.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPreviousPage) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNextPage) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  if (!user || !hasPermission('Proveedores', 'ver')) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error al cargar los proveedores</div>
          <button
            onClick={() => refetch()}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Gestión de Proveedores</h1>
            <p className="text-gray-600 mt-1">Administra los proveedores de la farmacia</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isFetching && searchTerm ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
              ) : (
                <Search className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar por nombre, NIT, contacto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm placeholder-gray-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            {/* Counter badge */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <div className="text-sm">
                  {searchTerm ? (
                    <div className="flex items-center space-x-1">
                      <span className="font-semibold text-emerald-700">
                        {paginationMeta?.total || suppliers.length}
                      </span>
                      <span className="text-gray-600">resultados encontrados</span>
                      <Search className="w-4 h-4 text-emerald-500 ml-1" />
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="font-semibold text-emerald-700">
                        {paginationMeta?.total || suppliers.length}
                      </span>
                      <span className="text-gray-600">proveedores registrados</span>
                      <Building2 className="w-4 h-4 text-emerald-500 ml-1" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Limit selector */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 font-medium">Mostrar</span>
              <div className="relative" data-dropdown>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all cursor-pointer flex items-center min-w-[3.5rem]"
                >
                  {limit}
                </button>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      isDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    {[10, 20, 30, 50, 100].map((value) => (
                      <div
                        key={value}
                        onClick={() => {
                          handleLimitChange(value);
                          setIsDropdownOpen(false);
                        }}
                        className="px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Create button */}
            {hasPermission('Proveedores', 'crear') && (
              <button
                onClick={handleOpenCreate}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Nuevo Proveedor</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="relative flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Nombre
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                NIT
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Teléfono
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Contacto
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Estado
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {suppliers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <Building2 className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500">No hay proveedores registrados</p>
                    <p className="text-gray-400 text-sm">Los proveedores aparecerán aquí.</p>
                  </div>
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => (
                <tr
                  key={supplier.id}
                  className="hover:bg-gray-100 transition-colors group"
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{supplier.nombre}</p>
                        {supplier.email && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" />
                            {supplier.email}
                          </p>
                        )}
                        {supplier.direccion && (
                          <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {supplier.direccion}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-700 font-mono">
                      {supplier.nit || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      {supplier.telefono || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      {supplier.contacto || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {supplier.activo ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Inactivo
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {hasPermission('Proveedores', 'editar') && (
                        <button
                          onClick={() => handleOpenEdit(supplier)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('Proveedores', 'eliminar') && (
                        <button
                          onClick={() => setSupplierToDelete(supplier)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Loading overlay */}
        {isFetching && suppliers.length > 0 && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-6 py-4 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-600 border-t-transparent"></div>
              <span className="text-sm font-medium text-gray-700">Buscando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {paginationMeta && paginationMeta.total > 0 && (
        <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
          <div className="flex items-center justify-between">
            {/* Results info */}
            <div className="flex items-center space-x-6">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">
                  {(paginationMeta.page - 1) * paginationMeta.limit + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                </span>
                <span className="mx-2">de</span>
                <span className="font-semibold text-gray-900">{paginationMeta.total}</span>
                <span className="ml-1">resultados</span>
              </div>

              <div className="text-sm text-gray-500">
                Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de{' '}
                <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
              </div>
            </div>

            {/* Pagination controls */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={!paginationMeta.hasPreviousPage}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPreviousPage
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              <button
                onClick={handlePreviousPage}
                disabled={!paginationMeta.hasPreviousPage}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPreviousPage
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Numeric pages */}
              <div className="flex items-center space-x-1 mx-2">
                {(() => {
                  const pages: React.ReactNode[] = [];
                  const maxVisiblePages = 3;
                  let start = Math.max(
                    1,
                    paginationMeta.page - Math.floor(maxVisiblePages / 2)
                  );
                  let end = Math.min(
                    paginationMeta.totalPages,
                    start + maxVisiblePages - 1
                  );

                  start = Math.max(1, end - maxVisiblePages + 1);

                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span
                          key="ellipsis-start"
                          className="w-9 h-9 flex items-center justify-center text-gray-400"
                        >
                          ⋯
                        </span>
                      );
                    }
                  }

                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all ${
                          i === paginationMeta.page
                            ? 'bg-emerald-600 text-white shadow-sm'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (end < paginationMeta.totalPages) {
                    if (end < paginationMeta.totalPages - 1) {
                      pages.push(
                        <span
                          key="ellipsis-end"
                          className="w-9 h-9 flex items-center justify-center text-gray-400"
                        >
                          ⋯
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={paginationMeta.totalPages}
                        onClick={() => handlePageChange(paginationMeta.totalPages)}
                        className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {paginationMeta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <button
                onClick={handleNextPage}
                disabled={!paginationMeta.hasNextPage}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNextPage
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => handlePageChange(paginationMeta.totalPages)}
                disabled={!paginationMeta.hasNextPage}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNextPage
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Drawer */}
      <SupplierDrawer
        isOpen={isCreateOpen}
        onClose={handleCloseDrawer}
        supplier={supplierToEdit}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Modal */}
      {supplierToDelete && (
        <DeleteModal
          supplier={supplierToDelete}
          onClose={() => setSupplierToDelete(null)}
          onConfirm={() => deleteMutation.mutate(supplierToDelete.id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
